<script setup lang="ts">
import { useTemplateRef } from 'vue';

interface Props {
  isWebcamReady: boolean;
  isShowingTakenPhoto: boolean;
  isTakenPhotoLoaded: boolean;
  photoUrl?: string;
}

interface Emits {
  photoLoad: [];
}

interface WebcamTargets {
  container: HTMLElement;
  video: HTMLVideoElement;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const props = defineProps<Props>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<Emits>();

const webcamContainer = useTemplateRef<HTMLElement>('webcamContainer');
const webcamVideo = useTemplateRef<HTMLVideoElement>('webcamVideo');

function getWebcamTargets(): WebcamTargets {
  const container = webcamContainer.value;
  const video = webcamVideo.value;
  if (!(container && video)) {
    throw new Error('Webcam capture stage is not mounted');
  }

  return {
    container,
    video,
  };
}

defineExpose({ getWebcamTargets });
</script>

<template>
  <div
    ref="webcamContainer"
    class="PhotoFromWebcam__webcam absolute-inset-0 sentry-block"
    data-testid="webcam-capture-stage"
  >
    <video
      ref="webcamVideo"
      autoplay
      class="PhotoFromWebcam__webcam-video absolute-inset-0"
      data-testid="webcam-capture-video"
      muted
      playsinline
    />
    <span
      class="PhotoFromWebcam__webcam-placeholder absolute-inset-0"
      :class="{
        'PhotoFromWebcam__webcam-placeholder--hidden': props.isWebcamReady,
      }"
    />
  </div>

  <img
    v-if="props.isShowingTakenPhoto"
    class="PhotoFromWebcam__photo absolute-inset-0 sentry-block"
    :class="{
      'PhotoFromWebcam__photo--loaded': props.isTakenPhotoLoaded,
    }"
    data-testid="webcam-captured-photo"
    :src="props.photoUrl"
    @load="emit('photoLoad')"
  />
</template>

<style scoped lang="scss">
.PhotoFromWebcam__webcam {
  object-fit: cover;
  width: var(--photo-from-webcam-stage-width, auto);
  height: var(--photo-from-webcam-stage-height, auto);
}

.PhotoFromWebcam__webcam-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  // Mirror the preview so users see themselves as in a mirror.
  // The captured photo is also mirrored (in PhotoProcessing.js) so
  // what users see in the preview matches the final result.
  transform: var(--photo-from-webcam-video-transform, scaleX(-1));
}

.PhotoFromWebcam__webcam-placeholder {
  background: #000000;
  transition: opacity 200ms;

  &--hidden {
    opacity: 0;
    pointer-events: none;
  }
}

.PhotoFromWebcam__photo {
  width: var(--photo-from-webcam-stage-width, 100%);
  height: var(--photo-from-webcam-stage-height, 100%);
  object-fit: cover;
  z-index: 1;
  /* Prevent flash/glitch during image load */
  opacity: 0;
  transition: opacity 200ms;

  &--loaded {
    opacity: 1;
  }
}

.PhotoFromWebcam__webcam,
.PhotoFromWebcam__photo {
  top: var(--photo-from-webcam-stage-top, 0);
  left: var(--photo-from-webcam-stage-left, 0);
  max-width: var(--photo-from-webcam-stage-max-width, none);
  max-height: var(--photo-from-webcam-stage-max-height, none);
  overflow: var(--photo-from-webcam-stage-overflow, visible);
  border-radius: var(--photo-from-webcam-stage-border-radius, 0);
  transform: var(--photo-from-webcam-stage-transform, none);
}

</style>
